'use client';

import { PageInfo } from '~/services/job-search/fetch-jobs';
import { JobSearchPcAccordion } from '~/components/accordion/job-search-pc-accordion';
import { useState, useEffect } from 'react';
import { JobSearchDrawer } from '~/components/drawer/job-search-drawer';
import { mapAreaPrefectures } from '~/lib/map-area-prefectures';
import { mapNestedLicenses } from '~/lib/map-nested-licenses';
import {
  formatAnnualSalaryBySymbol,
  generateJobSearchTitle,
} from '~/lib/utils';
import { PopularLinks } from '../popular-links';
import type { PopularLinks as PopularLinksType } from '~/services/job-search/fetch-jobs';
import type { MasterData, ParentItem } from '~/types/master-data';
import type { SearchConditions } from '~/types/search-conditions';
import type { ConditionParam } from '~/types/condition-param';
import { isMobile } from 'react-device-detect';
interface JobSearchFormProps {
  conditionText: string;
  pageInfo: PageInfo;
  masterData: MasterData;
  conditionParams: ConditionParam[];
  lastJobIndex: number;
  popularLinks: PopularLinksType;
}

export default function JobSearchForm(props: JobSearchFormProps) {
  const [searchConditions, setSearchConditions] = useState<SearchConditions>(
    generateSearchConditions(props.conditionParams),
  );

  const [isClientMobile, setIsClientMobile] = useState(false);

  useEffect(() => {
    setIsClientMobile(isMobile);
  }, []);

  const mappedAreas = mapAreaPrefectures(
    props.masterData.areas ?? [],
    props.masterData.prefectures ?? [],
  );
  const mappedLicenses = mapNestedLicenses(props.masterData.licenses ?? []);

  const updateChecked = (
    type: keyof SearchConditions,
    name: string,
    checked: boolean,
  ) => {
    setSearchConditions((prevSearchConditions) => {
      const currentCondition = prevSearchConditions[type] as string[];
      return {
        ...prevSearchConditions,
        [type]: checked
          ? [...(currentCondition ?? []), name]
          : (currentCondition ?? []).filter(
              (existingName) => existingName !== name,
            ),
      };
    });
  };

  // 親項目のチェックに応じてchildrenのチェックを一括更新
  const updateParentChecked = (
    item: ParentItem,
    type: keyof SearchConditions,
    checked: boolean,
  ) => {
    setSearchConditions((prevSearchConditions) => {
      const updatedNames = checked
        ? [
            ...(prevSearchConditions[type] as string[]),
            ...(item.children?.map((child) => child.name) || []), // 子供の名前を追加（親は追加しない）
          ]
        : (prevSearchConditions[type] as string[]).filter(
            (name) =>
              name !== item.name &&
              !item.children?.some((child) => child.name === name),
          );

      return {
        ...prevSearchConditions,
        [type]: updatedNames,
      };
    });
  };

  const resetConditions = () => {
    setSearchConditions({
      freeWord: '',
      annualSalaryMin: null,
      annualSalaryMax: null,
      categoryNames: [],
      typeNames: [],
      licenseNames: [],
      prefectureNames: [],
      contractNames: [],
      featureNames: [],
      cityNames: [],
    });
  };

  return (
    <div className="mb-4 px-2 p-conditions -v2">
      <h1 className="p-job-search__title u-pl0">
        {generateJobSearchTitle(props.conditionText, props.conditionParams)}
        転職・求人一覧
      </h1>
      <h2 className="p-job-search__result-count md:hidden">
        検索結果
        <span className="p-job-search__result-count__count">
          {props.pageInfo.total_count}
        </span>
        件<span className="u-dis-pc-only">中</span>
        <span className="normal u-dis-pc-only">1-{props.lastJobIndex}件</span>
      </h2>
      <div className="u-mt10 p-conditions__current-condition">
        <span className="p-conditions__highlighted -v2">
          現在の検索条件:&nbsp;
        </span>
        <span className="p-conditions__selected-conditions">
          {formatAnnualSalaryBySymbol(props.conditionText)}
        </span>
        {isClientMobile && (
          <JobSearchDrawer
            masterData={props.masterData}
            categoryNamesSet={new Set(searchConditions.categoryNames)}
            mappedAreas={mappedAreas}
            mappedLicenses={mappedLicenses}
            searchConditions={searchConditions}
            setSearchConditions={setSearchConditions}
            updateParentChecked={updateParentChecked}
            updateChecked={updateChecked}
            resetConditions={resetConditions}
          />
        )}
      </div>

      {isClientMobile && (
        <div className="mt-4">
          <PopularLinks popularLinks={props.popularLinks} />
        </div>
      )}

      {!isClientMobile && (
        <JobSearchPcAccordion
          masterData={props.masterData}
          mappedAreas={mappedAreas}
          mappedLicenses={mappedLicenses}
          conditionParams={props.conditionParams}
          updateParentChecked={updateParentChecked}
          updateChecked={updateChecked}
          resetConditions={resetConditions}
          searchConditions={searchConditions}
          setSearchConditions={setSearchConditions}
        />
      )}
    </div>
  );
}

function generateSearchConditions(
  conditionParams: ConditionParam[],
): SearchConditions {
  const names: SearchConditions = {
    freeWord: '',
    annualSalaryMin: null,
    annualSalaryMax: null,
    categoryNames: [],
    typeNames: [],
    licenseNames: [],
    prefectureNames: [],
    contractNames: [],
    featureNames: [],
    cityNames: [],
  };

  for (const param of conditionParams) {
    switch (param.type) {
      case 'category':
        names.categoryNames.push(param.name);
        break;
      case 'type':
        names.typeNames.push(param.name);
        break;
      case 'license':
        names.licenseNames.push(param.name);
        break;
      case 'prefecture':
        names.prefectureNames.push(param.name);
        break;
      case 'contract':
        names.contractNames.push(param.name);
        break;
      case 'feature':
        names.featureNames.push(param.name);
        break;
      case 'annualSalaryMin':
        names.annualSalaryMin = param.id;
        break;
      case 'annualSalaryMax':
        names.annualSalaryMax = param.id;
        break;
      default:
        break;
    }
  }

  return names;
}
